import { useReducedMotion } from "framer-motion";

// The site's one easing curve. Every reveal, the hero entrance and the route
// entrance share it, so two elements moving at the same moment never travel on
// different curves. Changing it here changes the feel of the whole site.
export const EASE = [0.16, 1, 0.3, 1];

// How long a reveal takes, and how far it travels. Extracted as names because
// the route entrance in SiteLayout deliberately uses a shorter duration and no
// distance at all — having the defaults named makes that departure legible.
export const REVEAL_DURATION = 0.7;
export const REVEAL_DISTANCE = 20;

// Cards may also settle in from slightly small. Text may not.
//
// A paragraph that grows as it arrives reads as being pushed into the page; a
// card that grows reads as settling onto it. The difference is that a card has
// edges, so the scale is legible as the object's own size rather than as the
// type changing size. Opt in per call site — `reveal(delay, { scale: true })` —
// so the default stays exactly what every existing caller already gets.
export const REVEAL_SCALE = 0.96;

// Reveals that fire when the element scrolls into view.
//
// `margin: "-60px"` starts the movement slightly before the element reaches the
// fold, so it has settled by the time it is comfortably readable. `once: true`
// means a section never replays on the way back up — the hero is the deliberate
// exception and drives its own animation, in Hero.jsx.
//
// Returns {} under reduced motion, so the caller spreads nothing and the element
// renders static rather than animating to the same place instantly.
export const useReveal = () => {
    const reduceMotion = useReducedMotion();

    return (delay = 0, { scale = false } = {}) =>
        reduceMotion
            ? {}
            : {
                  // The scale keys are spread in rather than set to 1: naming a
                  // property here at all makes framer-motion animate it, so an
                  // unconditional `scale: 1` would drive a no-op animation on
                  // every text reveal on the site.
                  initial: { opacity: 0, y: REVEAL_DISTANCE, ...(scale && { scale: REVEAL_SCALE }) },
                  whileInView: { opacity: 1, y: 0, ...(scale && { scale: 1 }) },
                  viewport: { once: true, margin: "-60px" },
                  transition: { duration: REVEAL_DURATION, ease: EASE, delay },
              };
};

// The same movement, played on mount instead of on scroll.
//
// For blocks already on screen when the page opens. A scroll trigger there
// either fires instantly anyway, or — on a page short enough that the element
// never crosses the margin — never fires at all, leaving the content invisible.
export const useEnter = () => {
    const reduceMotion = useReducedMotion();

    return (delay = 0) =>
        reduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: REVEAL_DISTANCE },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: REVEAL_DURATION, ease: EASE, delay },
              };
};
